import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import VisibilityIcon from "@mui/icons-material/Visibility";

const NotificationCardView = ({
  notifications,
  loading,
  onEdit,
  onDelete,
  onView,
  onResolve,
  formatDate,
  getTypeLabel,
  getSeverityColor,
  type = "client", // "client" or "admin"
}) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!notifications || notifications.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
          p: 3,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No notifications found
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        pb: 4,
      }}
    >
      {notifications.map((notification) => {
        const isUnread = type === "client" && !notification.read;
        const isResolved = type === "admin" && notification.resolved;

        return (
          <Card
            key={notification.id}
            sx={{
              width: "100%",
              boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
              borderRadius: "12px",
              overflow: "visible",
              bgcolor: isUnread
                ? "rgba(12, 44, 80, 0.15)"
                : isResolved
                ? "rgba(128, 128, 128, 0.1)"
                : "background.paper",
              border: isUnread
                ? "2px solid rgba(12, 44, 80, 0.5)"
                : "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              {/* Header: Title and Status */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 1.5,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "18px",
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    {notification.title}
                  </Typography>
                  {isUnread && (
                    <Chip
                      label="Unread"
                      size="small"
                      sx={{
                        backgroundColor: "rgba(12, 44, 80, 0.3)",
                        color: "#fff",
                        fontSize: "10px",
                        height: "20px",
                        mt: 0.5,
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {type === "client" && (
                    <Chip
                      label={getTypeLabel(notification.type)}
                      size="small"
                      sx={{ fontSize: "11px", height: "24px" }}
                    />
                  )}
                  {type === "admin" && (
                    <Chip
                      label={notification.severity || "medium"}
                      size="small"
                      color={getSeverityColor(notification.severity)}
                      sx={{ fontSize: "11px", height: "24px" }}
                    />
                  )}
                </Box>
              </Box>

              {/* Date */}
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <NotificationsIcon
                    sx={{ fontSize: "18px", color: "text.secondary" }}
                  />
                  <Typography variant="body2" sx={{ fontSize: "13px" }}>
                    {formatDate(notification.date || notification.created_at)}
                  </Typography>
                </Box>
              </Box>

              {/* User (for client notifications) */}
              {type === "client" && notification.user_name && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>
                    User:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "13px", mt: 0.5 }}>
                    {notification.user_name || "Unknown"}
                  </Typography>
                  {notification.user_email && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "12px" }}
                    >
                      {notification.user_email}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Message/Body */}
              <Box
                sx={{
                  mb: 1.5,
                  p: 1.5,
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  borderRadius: "8px",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "13px",
                    color: "text.secondary",
                    lineHeight: 1.6,
                  }}
                >
                  {notification.body || notification.message || "-"}
                </Typography>
              </Box>

              {/* Status (for admin alerts) */}
              {type === "admin" && (
                <Box sx={{ mb: 1.5 }}>
                  <Chip
                    label={isResolved ? "Resolved" : "Active"}
                    size="small"
                    color={isResolved ? "default" : "warning"}
                    sx={{ fontSize: "11px", height: "24px" }}
                  />
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "space-between",
                }}
              >
                {type === "client" && (
                  <>
                    <Tooltip title="View Full">
                      <IconButton
                        size="medium"
                        onClick={() => onView && onView(notification)}
                        sx={{
                          background: "rgba(12, 44, 80, 0.2)",
                          color: "#0C2C50",
                          flex: 1,
                          height: "44px",
                          borderRadius: "8px",
                          "&:hover": {
                            background: "rgba(12, 44, 80, 0.3)",
                          },
                        }}
                      >
                        <VisibilityIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="medium"
                        onClick={() => onEdit && onEdit(notification)}
                        sx={{
                          background: "rgba(42, 245, 152, 0.2)",
                          color: "#2af598",
                          flex: 1,
                          height: "44px",
                          borderRadius: "8px",
                          "&:hover": {
                            background: "rgba(42, 245, 152, 0.3)",
                          },
                        }}
                      >
                        <EditIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="medium"
                        onClick={() => onDelete && onDelete(notification.id)}
                        sx={{
                          background: "rgba(244, 67, 54, 0.2)",
                          color: "#f44336",
                          flex: 1,
                          height: "44px",
                          borderRadius: "8px",
                          "&:hover": {
                            background: "rgba(244, 67, 54, 0.3)",
                          },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}

                {type === "admin" && (
                  <>
                    {!isResolved && (
                      <Tooltip title="Resolve">
                        <IconButton
                          size="medium"
                          onClick={() => onResolve && onResolve(notification.id)}
                          sx={{
                            background: "rgba(76, 175, 80, 0.2)",
                            color: "#4CAF50",
                            flex: 1,
                            height: "44px",
                            borderRadius: "8px",
                            "&:hover": {
                              background: "rgba(76, 175, 80, 0.3)",
                            },
                          }}
                        >
                          <CheckCircleIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton
                        size="medium"
                        onClick={() => onDelete && onDelete(notification.id)}
                        sx={{
                          background: "rgba(244, 67, 54, 0.2)",
                          color: "#f44336",
                          flex: 1,
                          height: "44px",
                          borderRadius: "8px",
                          "&:hover": {
                            background: "rgba(244, 67, 54, 0.3)",
                          },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

export default NotificationCardView;
